import { useCallback, useEffect, useMemo, useState } from "react";
import { auditService } from "@/services";
import type { AuditActionType, AuditEntry } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { canViewAudit } from "@/config/roles";
import { PageHeader } from "@/components/feature/PageHeader";
import { SettingsNav } from "../components/SettingsNav";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { formatDateTime } from "@/utils/format";
import { exportReportCSV } from "@/utils/report";
import { AUDIT_ACTION_META, AUDIT_MODULES, getAuditActionMeta } from "../components/labels";
import { roleLabel } from "@/config/roles";

export default function AuditSettingsPage() {
  const { role } = useAuth();
  const canView = canViewAudit(role);

  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const loading = entries === null;
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [module, setModule] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const facets = await auditService.getAuditFacets();
      setAction((a) => (a && facets.actions.includes(a as AuditActionType) ? a : ""));
      setModule((m) => (m && facets.modules.includes(m) ? m : ""));
      setEntries(await auditService.listAudit());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the audit log.");
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!entries) return [];
    let out = entries;
    if (action) out = out.filter((e) => e.action === action);
    if (module) out = out.filter((e) => e.module === module);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(
        (e) =>
          e.narration.toLowerCase().includes(q) ||
          e.entity.toLowerCase().includes(q) ||
          e.userName.toLowerCase().includes(q)
      );
    }
    return out;
  }, [entries, action, module, search]);

  const facets = useMemo(() => {
    const actions = [...new Set((entries ?? []).map((e) => e.action))].sort();
    return { actions };
  }, [entries]);

  const handleExport = () => {
    exportReportCSV({
      filename: "audit-log",
      reportName: "Audit log",
      filters: [`Action: ${action || "All"}`, `Module: ${module || "All"}`, `Search: ${search || "—"}`],
      columns: [
        { header: "Timestamp", value: (r) => formatDateTime(String(r.timestamp)) },
        { header: "User", value: (r) => String(r.userName) },
        { header: "Role", value: (r) => roleLabel(r.userRole as never) },
        { header: "Action", value: (r) => getAuditActionMeta(r.action as AuditActionType).label },
        { header: "Module", value: (r) => String(r.module) },
        { header: "Record", value: (r) => String(r.entity) },
        { header: "Narration", value: (r) => String(r.narration) },
      ],
      rows: filtered.map((e) => ({
        timestamp: e.timestamp,
        userName: e.userName,
        userRole: e.userRole,
        action: e.action,
        module: e.module,
        entity: e.entity,
        narration: e.narration,
      })),
    });
  };

  const columns: DataTableColumn<AuditEntry>[] = [
    {
      id: "timestamp",
      header: "Timestamp",
      accessor: (e) => <span className="tnum whitespace-nowrap">{formatDateTime(e.timestamp)}</span>,
      sortValue: (e) => e.timestamp,
      sortable: true,
      width: "160px",
    },
    {
      id: "user",
      header: "User",
      accessor: (e) => (
        <div>
          <p className="font-medium text-foreground-900">{e.userName}</p>
          <p className="text-xs text-foreground-500">{roleLabel(e.userRole)}</p>
        </div>
      ),
      sortValue: (e) => e.userName,
      sortable: true,
    },
    {
      id: "action",
      header: "Action",
      accessor: (e) => {
        const meta = getAuditActionMeta(e.action);
        return <Badge color={meta.badge}>{meta.label}</Badge>;
      },
      sortValue: (e) => getAuditActionMeta(e.action).label,
      sortable: true,
    },
    {
      id: "module",
      header: "Module",
      accessor: (e) => <span className="text-foreground-800">{e.module}</span>,
      sortValue: (e) => e.module,
      sortable: true,
    },
    {
      id: "record",
      header: "Record",
      accessor: (e) => <span className="whitespace-nowrap text-foreground-800">{e.entity}</span>,
      sortValue: (e) => e.entity,
      sortable: true,
    },
    {
      id: "narration",
      header: "Narration",
      accessor: (e) => (
        <div>
          <p className="text-foreground-800">{e.narration}</p>
          {e.changes && e.changes.length ? (
            <p className="mt-0.5 text-xs font-medium text-primary-600">{e.changes.length} field change(s)</p>
          ) : null}
        </div>
      ),
      sortValue: (e) => e.narration,
      sortable: true,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Organization configuration, staff users and the audit trail."
        actions={
          canView ? (
            <Button variant="outline" icon={<i className="ri-download-2-line" />} onClick={handleExport} disabled={filtered.length === 0}>
              Export log
            </Button>
          ) : null
        }
      />
      <SettingsNav />

      {!canView ? (
        <EmptyState icon="ri-lock-line" title="Admin & Super Admin only" description="Only Admin and Super Admin may view the audit log." />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !entries ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <Card title="Audit log" subtitle="A read-only record of every significant action. It can never be edited or deleted from the interface.">
          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="relative sm:col-span-1">
              <i className="ri-search-line pointer-events-none absolute inset-y-0 left-0 flex w-8 items-center justify-center text-sm text-foreground-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search narration, record, user…"
                className="h-9 w-full rounded-md border border-background-200 bg-background-50 pl-8 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
              />
              {loading ? (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex w-7 items-center justify-center text-primary-600">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                </span>
              ) : search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 flex w-7 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              ) : null}
            </div>
            <Select
              value={action}
              onChange={setAction}
              placeholder="All actions"
              options={[{ value: "", label: "All actions" }, ...facets.actions.map((a) => ({ value: a, label: getAuditActionMeta(a).label }))]}
            />
            <Select
              value={module}
              onChange={setModule}
              placeholder="All modules"
              options={[{ value: "", label: "All modules" }, ...AUDIT_MODULES.map((m) => ({ value: m, label: m }))]}
            />
          </div>
          <DataTable<AuditEntry>
            columns={columns}
            rows={filtered}
            rowKey={(e) => e.id}
            emptyMessage="No matching entries"
            emptyDescription="Try clearing a filter or widening the search — the log never hides records, it only filters."
            showTotals
            totalLabel={`${filtered.length} entries`}
            defaultPageSize={15}
            showDensityToggle
            showColumnVisibility
          />
        </Card>
      )}
    </div>
  );
}